
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import secureLocalStorage from "./storage";
import { ToastContainer, toast } from "react-toastify";
import Shearchbar from "./Shearchbar";
import Navbar from "./Navbar";
import Footer from "./Footer";
import Security from "./Security";
import Manubar from "./Manubar";
import "./loader.css";
import "./shubh.css";

const itemsPerPage = 8;
const TABS = ["Popular", "Discount", "High Price", "Low Price"];

// ---------------------------------------------------------------------
// One theme per store: card gradient + logo markup.
// Driven entirely by the `?store=` URL param since the backend doesn't
// yet tag each product with its own store - once it does, swap this for
// a per-product lookup instead of a page-wide one.
// ---------------------------------------------------------------------
const STORE_THEMES = {
  Amazon: {
    gradient: "linear-gradient(135deg, #ff7a30 0%, #f4511e 100%)",
    logo: (
      <span style={{ fontWeight: 700, color: "#111" }}>
        amazon<span style={{ color: "#f4511e" }}>.in</span>
      </span>
    ),
  },
  Myntra: {
    gradient: "linear-gradient(135deg, #7b2ff7 0%, #b721ff 100%)",
    logo: (
      <span style={{ fontWeight: 700, color: "#e2298f" }}>
        <span style={{ color: "#ff5722" }}>M</span> Myntra
      </span>
    ),
  },
  Meesho: {
    gradient: "linear-gradient(135deg, #6a1b4d 0%, #3f0d29 100%)",
    logo: (
      <span style={{ fontWeight: 700, color: "#7c1fa2" }}>
        <span
          style={{
            background: "#f5540c",
            color: "#fff",
            borderRadius: "4px",
            padding: "0 6px",
            marginRight: "4px",
          }}
        >
          m
        </span>
        meesho
      </span>
    ),
  },
  Flipkart: {
    gradient: "linear-gradient(135deg, #2874f0 0%, #0d47a1 100%)",
    logo: (
      <span style={{ fontWeight: 700, color: "#2874f0" }}>
        Flipkart <span style={{ color: "#f5c800" }}>⚡</span>
      </span>
    ),
  },
  Ajio: {
    gradient: "linear-gradient(135deg, #d9a441 0%, #b5651d 100%)",
    logo: <span style={{ fontWeight: 800, color: "#111" }}>AJIO</span>,
  },
};

const normalizeStore = (name) => {
  if (!name) return "Amazon";
  const match = Object.keys(STORE_THEMES).find(
    (s) => s.toLowerCase() === name.toLowerCase()
  );
  return match || "Amazon";
};

const Shop = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const storeName = normalizeStore(searchParams.get("store"));
  const theme = STORE_THEMES[storeName];

  const _id = secureLocalStorage.getItem("n_id");
  const token = secureLocalStorage.getItem("n_token");

  const [product_details, setproduct_details] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  // Tabs (Popular / Discount / High Price / Low Price)
  const [activeTab, setActiveTab] = useState("Popular");

  // Price range filter
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [appliedMin, setAppliedMin] = useState(null);
  const [appliedMax, setAppliedMax] = useState(null);

  // Brand filter (checkbox + chip + search box)
  const [brandChecked, setBrandChecked] = useState(true);
  const [brandSearch, setBrandSearch] = useState("");

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const data = { user_id: _id };
      const res = await axios.post(
        `${process.env.REACT_APP_API}website/all_product_list`,
        data,
        { headers: { token } }
      );
      if (res?.data?.result) {
        setproduct_details(res.data.data);
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log("Request aborted");
      } else {
        console.log(error);
      }
    }
  };

  // ---- Sort by active tab ----
  let sortedProducts = [...product_details];
  if (activeTab === "Discount") {
    sortedProducts.sort((a, b) => (b.offer_percent || 0) - (a.offer_percent || 0));
  } else if (activeTab === "High Price") {
    sortedProducts.sort((a, b) => (b.price?.[0] || 0) - (a.price?.[0] || 0));
  } else if (activeTab === "Low Price") {
    sortedProducts.sort((a, b) => (a.price?.[0] || 0) - (b.price?.[0] || 0));
  }

  // ---- Apply price range filter ----
  let filteredProducts = sortedProducts;
  if (appliedMin !== null) {
    filteredProducts = filteredProducts.filter((p) => (p.price?.[0] || 0) >= appliedMin);
  }
  if (appliedMax !== null) {
    filteredProducts = filteredProducts.filter((p) => (p.price?.[0] || 0) <= appliedMax);
  }

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const handleChangePage = (newPage) => setCurrentPage(newPage);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredProducts.slice(startIndex, endIndex);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handleFilterGo = () => {
    setAppliedMin(minPrice ? Number(minPrice) : null);
    setAppliedMax(maxPrice ? Number(maxPrice) : null);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setMinPrice("");
    setMaxPrice("");
    setAppliedMin(null);
    setAppliedMax(null);
    setActiveTab("Popular");
    setBrandChecked(true);
    setBrandSearch("");
    setCurrentPage(1);
  };

  const clearBrand = () => {
    searchParams.delete("store");
    setSearchParams(searchParams);
  };

  const goToProduct = (id) => navigate(`/single_products/${id}`);

  const handleGrabDeal = (e) => {
    e.stopPropagation();
    navigate("/login");
  };

  // ---------------------------------------------------------------------
  // Brand list for the sidebar. Right now the page only ever represents
  // one store, so this is a single-item array. Once the API returns an
  // actual list of brands (e.g. per category), replace this array with
  // that response - the search box below already filters by `.name`, so
  // no other changes will be needed.
  // ---------------------------------------------------------------------
  const brandList = [{ name: `${storeName} India`, count: sortedProducts.length }];
  const visibleBrands = brandList.filter((b) =>
    b.name.toLowerCase().includes(brandSearch.toLowerCase())
  );

  return (
    <div>
      <ToastContainer position="top-center" />

      <div className="modal fade offcanvas-modal" id="exampleModal">
        <div className="modal-dialog offcanvas-dialog">
          <div className="modal-content">
            <Manubar />
          </div>
        </div>
      </div>
      {/* Breadcrumb */}
      <nav className="breadcrumb-section">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <ol className="breadcrumb bg-transparent m-0 p-0 align-items-center">
                <li className="breadcrumb-item">
                  <Link to="/">Home</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {storeName} - Top Deals
                </li>
              </ol>
            </div>
          </div>
        </div>
      </nav>

      <div className="container">
        <h2 className="text-center fw-bold my-4">{storeName} - Top Deals</h2>

        <div className="row">
          {/* ---------------- Filters Sidebar ---------------- */}
          <div className="col-lg-3 col-md-4 mb-4">
            {/* Clear only shows once a non-default filter is active
                (any tab other than Popular) - matches reference site */}
            {activeTab !== "Popular" && (
              <div className="d-flex justify-content-end align-items-center mb-2">
                <button
                  onClick={handleClearFilters}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#f4511e",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                >
                  Clear
                </button>
              </div>
            )}

            <div
              style={{
                border: "1px solid #eee",
                borderRadius: 10,
                padding: 16,
              }}
            >
              <h6 className="fw-bold mb-3">Filters</h6>
              <div className="text-muted mb-2" style={{ fontSize: 13 }}>
                {storeName} - Top Deals
              </div>

              <hr />

              <div className="mb-3">
                <div
                  className="d-flex justify-content-between align-items-center mb-2"
                  style={{ cursor: "pointer" }}
                >
                  <strong style={{ fontSize: 14 }}>Price</strong>
                </div>
                <div className="d-flex gap-2 align-items-center">
                  <input
                    type="number"
                    placeholder="Min"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                    className="form-control form-control-sm"
                  />
                  <span>-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                    className="form-control form-control-sm"
                  />
                  <button
                    onClick={handleFilterGo}
                    className="btn btn-sm"
                    style={{ background: "#f4511e", color: "#fff" }}
                  >
                    GO
                  </button>
                </div>
              </div>

              <hr />

              <div>
                <strong style={{ fontSize: 14 }}>Brand</strong>

                <input
                  type="text"
                  placeholder="Search Brand"
                  value={brandSearch}
                  onChange={(e) => setBrandSearch(e.target.value)}
                  className="form-control form-control-sm mt-2 mb-2"
                />

                {visibleBrands.map((b) => (
                  <div className="form-check mt-1" key={b.name}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={brandChecked}
                      onChange={() => setBrandChecked((prev) => !prev)}
                      id={`brandCheck-${b.name}`}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`brandCheck-${b.name}`}
                      style={{ fontSize: 14 }}
                    >
                      {b.name} ({b.count})
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ---------------- Products Section ---------------- */}
          <div className="col-lg-9 col-md-8">
            {/* Tabs */}
            {/* <div className="d-flex" style={{ borderBottom: "1px solid #eee", marginBottom: 16 }}>
              {TABS.map((tab) => (
                <button
                  key={tab}
                  onClick={() => handleTabClick(tab)}
                  style={{
                    background: "none",
                    border: "none",
                    padding: "12px 20px",
                    fontWeight: 600,
                    fontSize: 14,
                    color: activeTab === tab ? "#f4511e" : "#555",
                    borderBottom: activeTab === tab ? "3px solid #f4511e" : "3px solid transparent",
                    cursor: "pointer",
                  }}
                >
                  {tab}
                </button>
              ))}
            </div> */}
<div className="sort-tabs">
  {TABS.map((tab) => (
    <button
      key={tab}
      onClick={() => handleTabClick(tab)}
      className={`sort-tab ${activeTab === tab ? "active" : ""}`}
    >
      {tab}
    </button>
  ))}
</div>
            {/* Active brand chip - only visible while the brand checkbox
                is checked; unchecking it or clicking × hides it (same
                toggle, both paths call setBrandChecked(false)) */}
            {brandChecked && (
              <div className="mb-3">
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    border: "1px solid #ddd",
                    borderRadius: 20,
                    padding: "4px 12px",
                    fontSize: 13,
                    fontWeight: 500,
                  }}
                >
                  BRAND: {storeName} India
                  <span
                    onClick={() => setBrandChecked(false)}
                    style={{ cursor: "pointer", fontWeight: 700 }}
                  >
                    ×
                  </span>
                </span>
              </div>
            )}

            {/* Product Grid */}
            <div className="row">
              {currentData.length > 0 ? (
                currentData.map((items) => (
                  <div className="col-xl-3 col-lg-4 col-md-6 col-12 mb-4" key={items._id}>
                    <div
                      onClick={() => goToProduct(items._id)}
                      style={{
                            margin: "5px",
                        background: theme.gradient,
                        borderRadius: "14px",
                        padding: "14px",
                        minHeight: "190px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        cursor: "pointer",
                        boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
                      }}
                    >
                      {/* Store logo badge */}
                      <div
                        style={{
                          background: "#fff",
                          borderRadius: "6px",
                          padding: "4px 10px",
                          width: "fit-content",
                          fontSize: "13px",
                        }}
                      >
                        {theme.logo}
                      </div>

                      {/* Product info + image */}
                      <div className="d-flex align-items-center justify-content-between mt-2">
                        <div>
                          <div style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
                            {items.product_name}
                          </div>
                          <div style={{ color: "#fff", fontSize: 12, opacity: 0.85, marginBottom: 6 }}>
                            {items.quantity?.[0]}
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <span
                              style={{
                                background: "#fff",
                                borderRadius: 6,
                                padding: "2px 8px",
                                fontWeight: 700,
                                fontSize: 13,
                              }}
                            >
                              ₹{items.price?.[0]}
                            </span>
                            {items.offer_percent > 0 && (
                              <span
                                style={{
                                  color: "#fff",
                                  textDecoration: "line-through",
                                  fontSize: 12,
                                  opacity: 0.85,
                                }}
                              >
                                ₹{items.mrp}
                              </span>
                            )}
                          </div>
                        </div>

                        <div
                          style={{
                            background: "#fff",
                            borderRadius: "10px",
                            padding: "6px",
                            width: "70px",
                            height: "70px",
                            flexShrink: 0,
                          }}
                        >
                          <img
                            src={`${process.env.REACT_APP_API}uploads/${items.product_image}`}
                            alt={items.product_name}
                            style={{ width: "100%", height: "100%", objectFit: "contain" }}
                          />
                        </div>
                      </div>

                      {/* Grab Deal button */}
                      <button
                        onClick={handleGrabDeal}
                        style={{
                          alignSelf: "flex-end",
                          marginTop: "10px",
                          background: "#e8491d",
                          border: "none",
                          borderRadius: "20px",
                          padding: "6px 16px",
                          fontWeight: "700",
                          fontSize: "13px",
                          color: "#fff",
                          cursor: "pointer",
                        }}
                      >
                        Grab Deal →
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="row justify-content-center pb-5">
                  <div className="loader"></div>
                </div>
              )}
            </div>

            {/* Pagination */}
            <div className="text-center p-4">
              <button
                className="pagination-button"
                disabled={currentPage === 1}
                onClick={() => handleChangePage(currentPage - 1)}
              >
                Previous
              </button>
              {pageNumbers.map((page) => (
                <button
                  key={page}
                  onClick={() => handleChangePage(page)}
                  className={`pagination-button ${page === currentPage ? "pagiactive" : ""}`}
                >
                  {page}
                </button>
              ))}
              <button
                className="pagination-button"
                disabled={currentPage === totalPages}
                onClick={() => handleChangePage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* <Security /> */}
      <Footer />
    </div>
  );
};

export default Shop;
